import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';

import {
  getUserInfo,
  loginUserRequest,
  logoutUserRequest,
  confirmLoginWithGoogleRequest,
} from '@/api/user';
import { setClientProfileId } from '@/config/axios';
import { clearQueryCacheOnLogout } from '@/config/queryClientRegistry';
import { ADMIN_ROLE, IS_ADMIN_KEY } from '@/constants/roles';
import {
  getAccessToken,
  removeLocalStorageItem,
  storeAccessToken,
  removeAdminUserId,
  ADMIN_TOKEN_KEY,
} from '@/utils/localStorage';

import type { User } from '@/types/user';

type UserState = {
  isAuth: boolean;
  user: null | User;
  loading: boolean;
  /** Access token last successfully restored via getUserByToken / login. */
  sessionToken: string | null;
};

type AuthSuccessPayload = {
  user: User;
  access_token?: string;
  client_profile_id?: string;
};

type LoginErrorData = {
  message?: string;
  errors?: {
    errors?: { client_profile_id?: string };
    client_profile_id?: string;
  };
  client_profile_id?: string;
};

type LoginApiResponse = AuthSuccessPayload & {
  status?: number;
  data?: LoginErrorData;
  errors?: LoginErrorData;
  message?: string;
};

type LoginRejectValue = {
  error?: string;
  data?: LoginErrorData;
  errors?: LoginErrorData;
};

type GetUserInfoResponse = User & {
  status?: number;
};

const initialState = {
  isAuth: false,
  user: null,
  loading: true,
  sessionToken: null,
} as UserState;

const isProfileSelectionError = (
  payload: LoginRejectValue | undefined,
): boolean => {
  if (!payload) return false;

  return (
    payload.data?.errors?.errors?.client_profile_id ===
      'Profile selection required' ||
    payload.data?.errors?.client_profile_id === 'Profile selection required' ||
    payload.errors?.errors?.errors?.client_profile_id ===
      'Profile selection required' ||
    payload.errors?.errors?.client_profile_id === 'Profile selection required'
  );
};

const applyAuthSuccess = (state: UserState, payload: AuthSuccessPayload) => {
  if (payload.access_token) {
    storeAccessToken(payload.access_token);
    state.loading = false;
    state.isAuth = true;
    state.user = payload.user;
    state.sessionToken = payload.access_token;

    if (payload.client_profile_id && payload.user.role === 'client') {
      setClientProfileId(payload.client_profile_id);
    }
  } else {
    state.loading = false;
  }
};

export const loginUser = createAsyncThunk(
  'user/loginUser',
  async (data: object, { rejectWithValue }) => {
    const response = (await loginUserRequest(data)) as LoginApiResponse;

    // Check for error responses (no user in response means error)
    if (
      !response.user &&
      response.status &&
      (response.status === 401 || response.status === 400)
    ) {
      return rejectWithValue({
        error: response.data?.message,
        data: response.data,
      } satisfies LoginRejectValue);
    }

    // Also check if response itself is an error (no status but has errors)
    if (!response.user && response.errors) {
      return rejectWithValue({
        error: response.message,
        data: response,
      } satisfies LoginRejectValue);
    }

    return response;
  },
);

export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (): Promise<unknown> => {
    return logoutUserRequest();
  },
);

export const confirmLoginWithGoogle = createAsyncThunk(
  'user/confirmLoginWithGoogle',
  async (query: string, { rejectWithValue }) => {
    const response = (await confirmLoginWithGoogleRequest(
      query,
    )) as LoginApiResponse;
    if (response.status === 401 || response.status === 403) {
      return rejectWithValue({
        error: response.data?.message,
      } satisfies LoginRejectValue);
    }
    return response;
  },
);

export const getUserByToken = createAsyncThunk(
  'user/getUserByToken',
  async (token: string, { rejectWithValue }) => {
    const response = (await getUserInfo()) as GetUserInfoResponse;
    if (response.status === 401) {
      return rejectWithValue({
        error: 'Invalid token',
      } satisfies LoginRejectValue);
    }
    return response;
  },
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<AuthSuccessPayload>) => {
      state.isAuth = true;
      state.user = action.payload.user;
      state.loading = false;

      // Set authorization header if access_token is provided
      if (action.payload.access_token) {
        storeAccessToken(action.payload.access_token);
        state.sessionToken = action.payload.access_token;
      }

      // For client users, store the client_profile_id in localStorage
      if (
        action.payload.client_profile_id &&
        action.payload.user.role === 'client'
      ) {
        setClientProfileId(action.payload.client_profile_id);
      }
    },
    updateClientCompany: (state, action: PayloadAction<User>) => {
      state.isAuth = true;
      state.user = action.payload;
      state.loading = false;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      state.isAuth = true;
      state.user = state.user ? { ...state.user, ...action.payload } : null;
      state.loading = false;
    },
    finishUnauthenticatedBootstrap: (state) => {
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      applyAuthSuccess(state, action.payload);
    });
    builder.addCase(loginUser.rejected, (state, { payload }) => {
      if (isProfileSelectionError(payload as LoginRejectValue | undefined)) {
        state.loading = false;
        // Don't set isAuth = false or user = null - keep component mounted
        return;
      }

      // For other errors, clear auth state
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    });
    builder.addCase(logoutUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      removeLocalStorageItem('access_token');
      removeLocalStorageItem(IS_ADMIN_KEY);
      removeLocalStorageItem(ADMIN_TOKEN_KEY);
      removeAdminUserId();
      setClientProfileId(null);
      clearQueryCacheOnLogout();
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    });
    builder.addCase(logoutUser.rejected, (state) => {
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    });
    builder.addCase(confirmLoginWithGoogle.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(confirmLoginWithGoogle.fulfilled, (state, action) => {
      applyAuthSuccess(state, action.payload);
    });
    builder.addCase(confirmLoginWithGoogle.rejected, (state) => {
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    });
    builder.addCase(getUserByToken.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUserByToken.fulfilled, (state, action) => {
      if (getAccessToken() !== action.meta.arg) {
        // Embed replaced localStorage with a newer URL token while this
        // restore was in flight — do not overwrite the newer session.
        return;
      }
      state.loading = false;
      state.isAuth = true;
      state.user = action.payload;
      state.sessionToken = action.meta.arg;
      // Drop leftover switch-role markers once we are the real admin again.
      if (action.payload.role === ADMIN_ROLE) {
        removeLocalStorageItem(IS_ADMIN_KEY);
        removeLocalStorageItem(ADMIN_TOKEN_KEY);
        removeAdminUserId();
      }
    });
    builder.addCase(getUserByToken.rejected, (state, action) => {
      const failedToken = action.meta.arg;
      if (getAccessToken() !== failedToken) {
        // Embed replaced localStorage with a newer URL token while this
        // restore was in flight — do not wipe it or the restored session.
        return;
      }
      removeLocalStorageItem('access_token');
      removeLocalStorageItem(IS_ADMIN_KEY);
      removeLocalStorageItem(ADMIN_TOKEN_KEY);
      removeAdminUserId(); // Clear admin user ID
      setClientProfileId(null); // Clear client profile ID
      state.loading = false;
      state.isAuth = false;
      state.user = null;
      state.sessionToken = null;
    });
  },
});

export const {
  login,
  updateClientCompany,
  updateUser,
  finishUnauthenticatedBootstrap,
} = userSlice.actions;

export default userSlice.reducer;
